import {
    Alert, Avatar, Button, Clickable, Col, Divider, Icon, Row, Shoutout, Sidepanel, Spinner2,
    Text
} from 'components';
import { Api, capitalize, theme } from 'lib';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { PostPanel, StoryPanel, UserPanel } from '../';

// Removing a FanChat 2.0 topic or message reaches every member in real time, so it is confirmed.
const CONFIRMED_REMOVALS = ['fanchatTopic', 'fanchatMessage'];

const ReportPanel = ({ dataId, setDataId, closeModal }) => {
  const { t } = useTranslation();
  const [showUser, setShowUser] = useState(null),
    [showContent, setShowContent] = useState(null),
    [showRemoveConfirm, setShowRemoveConfirm] = useState(false),
    dispatch = useDispatch(),
    storedData = useSelector((state) => state.admin?.reports?.find((x) => x._id === dataId)),
    { createdAt, entityType, reported, description, owner, status, post, shoutout, story, fancam, comment, fanchat } =
      storedData || {};

  useEffect(() => {
    setShowContent(null);
    setShowRemoveConfirm(false);
  }, [dataId]);

  async function handleResolveReport(status) {
    if (status === 'contentRemoved' && entityType !== 'user') {
      const res = await Api.post('/admin/removeData', {
        type: capitalize(entityType),
        id: storedData[entityType]._id || storedData[entityType]
      });
    }

    const res = await Api.post('/admin/resolveReport', { status, reportId: dataId });
    if (!res) return;

    dispatch({
      type: 'ADMIN_EDIT_REPORT',
      payload: res
    });

    closeModal();
  }

  async function handleSelectUser(userId) {
    const res = await Api.post('/admin/getUser', { userId });
    if (!res) return;

    dispatch({
      type: 'ADMIN_ADD_USER',
      payload: res
    });

    setShowUser(res._id);
  }

  async function handleToggleBlock() {
    if (!reported?._id) return;
    const res = await Api.post('/admin/toggleBan', { userId: reported._id });
    if (!res) return;

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: res.banned ? 'User blocked.' : 'User unblocked.' }
    });

    const newStatus = res.banned ? 'blocked' : 'unblocked';
    await Api.post('/admin/resolveReport', { status: newStatus, reportId: dataId });

    dispatch({
      type: 'ADMIN_EDIT_REPORT',
      payload: {
        ...storedData,
        reported: { ...reported, banned: res.banned },
        status: newStatus
      }
    });
  }

  async function handleSelectPost(postId) {
    const res = await Api.post('/admin/getPost', { postId });
    if (!res) return;
    if (res.err)
      return dispatch({
        type: 'HANDLE_RES',
        payload: res
      });

    dispatch({
      type: 'ADMIN_ADD_POST',
      payload: res
    });

    setShowContent(res._id);
  }

  async function handleSelectStory(storyId) {
    const res = await Api.post('/admin/getStory', { storyId });

    if (!res) return;
    if (res.err)
      return dispatch({
        type: 'HANDLE_RES',
        payload: res
      });

    dispatch({
      type: 'ADMIN_ADD_STORY',
      payload: res
    });

    setShowContent(res._id);
  }

  async function handleRemoveShoutout(shoutoutId) {
    const res = await Api.post('/admin/removeData', {
      type: 'Shoutout',
      id: shoutoutId
    });

    if (!res) return;
  }

  return (
    <React.Fragment>
      <Sidepanel wid={600} pad="0" visible={dataId} closeModal={closeModal}>
        <Col pos="sticky" bg="#fff" zIndex="3" center top="0">
          <Row between center pad="12px 24px 0">
            <Text h3>{reported?.name}</Text>
          </Row>

          <Divider marg="12px 0 0" />
        </Col>

        {dataId && (typeof dataId === 'string' ? !storedData : false) && (
          <Col
            zIndex={5}
            pos="absolute"
            ht="100%"
            wid="100%"
            bg="rgba(210, 216, 223, 0.7)"
            left="0"
            centerAll
          >
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        )}

        <Col pad="24px" pos="relative">
          <Clickable onClick={() => handleSelectUser(reported._id)}>
            <Row start>
              <Avatar user={reported} />

              <Col noFlex marg="0 0 0 12px">
                <Text med>{reported?.name}</Text>

                {reported?.email?.includes('@') && <Text b2>{reported?.email}</Text>}

                <Text c1 med col={theme.GREY_60}>
                  @{reported?.username}
                </Text>
              </Col>
            </Row>
          </Clickable>

          <Row marg="24px 0 0" end start>
            <Text c1>Reported by</Text>

            <Clickable marg="0 0 0 4px" onClick={() => handleSelectUser(owner._id)}>
              <Text b2 med>
                {owner?.name}
              </Text>
            </Clickable>
          </Row>

          <React.Fragment>
            <Text b2 med marg="24px 0 4px">
              Description
            </Text>

            <Row pad="8px 16px" start>
              <Text col={!description && theme.GREY_60}>{description || 'No reason provided'}</Text>
            </Row>
          </React.Fragment>

          {status !== 'contentRemoved' && (
            <React.Fragment>
              {entityType === 'post' && (
                <Button
                  marg="16px 0 0"
                  text="Show reported post"
                  onClick={() => handleSelectPost(storedData.post._id)}
                />
              )}

              {entityType === 'shoutout' &&
                (showContent ? (
                  storedData.shoutout ? (
                    <Col marg="16px 0 0" noFlex>
                      <Shoutout item={storedData.shoutout} wide dontShowReact />

                      <Clickable
                        marg="0 0 0 auto"
                        wid="fit-content"
                        onClick={() => handleRemoveShoutout(storedData.shoutout._id)}
                      >
                        <Row center>
                          <Text col={theme.SECONDARY}>Remove shoutout</Text>

                          <Icon type="delete" col={theme.SECONDARY} />
                        </Row>
                      </Clickable>
                    </Col>
                  ) : (
                    <Col marg="16px 0 0" noFlex center>
                      <Text bold>Content removed</Text>
                    </Col>
                  )
                ) : (
                  <Button
                    marg="16px 0 0"
                    text="Show reported shoutout"
                    onClick={() => setShowContent(true)}
                  />
                ))}

              {entityType === 'story' && (
                <Button
                  marg="16px 0 0"
                  text="Show reported story"
                  onClick={() => handleSelectStory(storedData.story)}
                />
              )}

              {entityType === 'fancam' && (
                <Col marg="16px 0 0" noFlex>
                  <Text b2 med marg="0 0 8px">
                    {storedData?.fancam?.type === 'challenge' ? 'FanCam Challenge' : 'FanCam React'}
                  </Text>
                  {storedData?.fancam?.media?.url && (
                    storedData.fancam.media.type === 'video' ? (
                      <video src={storedData.fancam.media.url} controls style={{ width: '100%', maxHeight: 300 }} />
                    ) : (
                      <img src={storedData.fancam.media.url} alt="fancam media" style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }} />
                    )
                  )}
                </Col>
              )}

              {entityType === 'comment' && (
                <Col marg="16px 0 0" noFlex>
                  <Text b2 med marg="0 0 4px">Reported Comment</Text>
                  <Row pad="8px 16px" start>
                    <Text>{storedData?.comment?.body || 'Comment unavailable'}</Text>
                  </Row>
                </Col>
              )}

              {entityType === 'fanchat' && (
                <Col marg="16px 0 0" noFlex>
                  <Text b2 med marg="0 0 4px">Reported FanChat Message</Text>
                  <Row pad="8px 16px" start>
                    <Text>{storedData?.fanchat?.body || 'FanChat message unavailable'}</Text>
                  </Row>
                  {storedData?.fanchat?.media?.url && (
                    storedData.fanchat.media.type === 'video' ? (
                      <video src={storedData.fanchat.media.url} controls style={{ width: '100%', maxHeight: 300 }} />
                    ) : (
                      <img src={storedData.fanchat.media.url} alt="fanchat media" style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }} />
                    )
                  )}
                </Col>
              )}

              {entityType === 'fanchatTopic' && (
                <Col marg="16px 0 0" noFlex>
                  <Text b2 med marg="0 0 4px">{t('admin.reports.fanchatTopic.heading')}</Text>
                  {storedData?.fanchatTopic?.title ? (
                    <Col pad="8px 16px" noFlex>
                      <Text med>{storedData.fanchatTopic.title}</Text>
                      {storedData.fanchatTopic.description && (
                        <Text marg="4px 0 0">{storedData.fanchatTopic.description}</Text>
                      )}
                    </Col>
                  ) : (
                    <Row pad="8px 16px" start>
                      <Text>{t('admin.reports.fanchatTopic.unavailable')}</Text>
                    </Row>
                  )}
                </Col>
              )}

              {entityType === 'fanchatMessage' && (
                <Col marg="16px 0 0" noFlex>
                  <Text b2 med marg="0 0 4px">{t('admin.reports.fanchatMessage.heading')}</Text>
                  <Row pad="8px 16px" start>
                    <Text>
                      {storedData?.fanchatMessage?.text ||
                        storedData?.fanchatMessage?.reportedText ||
                        t('admin.reports.fanchatMessage.unavailable')}
                    </Text>
                  </Row>
                  {!storedData?.fanchatMessage?.text &&
                    storedData?.fanchatMessage?.reportedText && (
                      <Text c1 col={theme.GREY_60} marg="0 16px">
                        {t('admin.reports.fanchatMessage.deletedNote')}
                      </Text>
                    )}
                </Col>
              )}

            </React.Fragment>
          )}

          <Row gap="12px" marg="16px 0 0" wrap>
            {status !== 'contentRemoved' && entityType !== 'user' && (
              <Button
                text="Remove"
                icon="block"
                bg={theme.SECONDARY}
                onClick={() =>
                  CONFIRMED_REMOVALS.includes(entityType)
                    ? setShowRemoveConfirm(true)
                    : handleResolveReport('contentRemoved')
                }
              />
            )}

            <Button
              text={reported?.banned ? 'Unblock User' : 'Block User'}
              icon={reported?.banned ? 'check' : 'block'}
              bg={theme.SECONDARY}
              onClick={handleToggleBlock}
            />

            {status !== 'pending' && status !== 'blocked' && status !== 'unblocked' && (
              <Button
                text="Set To Pending"
                bordered
                onClick={() => handleResolveReport('pending')}
              />
            )}

            {status !== 'ignored' && status !== 'blocked' && status !== 'unblocked' && (
              <Button
                text="Ignore"
                icon="close"
                bordered
                onClick={() => handleResolveReport('ignored')}
              />
            )}

            {status === 'contentRemoved' && (
              <Text b2 col={theme.GREY_60} marg="8px 0 0">Content has been removed.</Text>
            )}
          </Row>
        </Col>
      </Sidepanel>

      {showRemoveConfirm && (
        <Alert
          text={t(`admin.reports.${entityType}.removeConfirm`)}
          acceptText={t('admin.reports.remove')}
          refuseText={t('admin.reports.cancel')}
          accept={() => {
            setShowRemoveConfirm(false);
            handleResolveReport('contentRemoved');
          }}
          refuse={() => setShowRemoveConfirm(false)}
        />
      )}

      <UserPanel
        dataId={showUser}
        setDataId={setShowUser}
        closeModal={() => setShowUser(null)}
        hasBack
      />

      {entityType === 'post' && (
        <PostPanel
          dataId={showContent}
          setDataId={setShowContent}
          closeModal={() => setShowContent(null)}
          hasBack
        />
      )}

      {entityType === 'story' && (
        <StoryPanel
          dataId={showContent}
          setDataId={setShowContent}
          closeModal={() => setShowContent(null)}
        />
      )}
    </React.Fragment>
  );
};

export default ReportPanel;
